import React, { createContext, useContext, useState } from 'react';
import {
  handleTestRegex,
  validateSvgFileDimensions,
} from '@aibees/shared/utils';
import {
  WhiteLabelContextData as ContextData,
  WhiteLabelContextProps as ContextProps,
  WhiteLabelFileFieldState,
  WhiteLabelTextFieldState,
} from './types';
import { whiteLabelFieldRegex } from '../utils/formRegex';
import { fieldErrorMessages, fileSize } from './config';

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const useWhiteLabelContext = () => useContext(context);

export const WhiteLabelProvider = ({ children }: ContextProps) => {
  const [companyDomain, setCompanyDomain] = useState<WhiteLabelTextFieldState>({
    value: 'app.ai-ark.com',
  });
  const [companyName, setCompanyName] = useState<WhiteLabelTextFieldState>({
    value: 'AI Ark',
  });
  const [primaryColor, setPrimaryColor] = useState<WhiteLabelTextFieldState>({
    value: '#1851EF',
  });
  const [notifBg, setNotifBg] = useState<WhiteLabelTextFieldState>({
    value: '#E3E9FF',
  });
  const [headerLogo, setHeaderLogo] = useState<WhiteLabelFileFieldState>({});
  const [loginLogo, setLoginLogo] = useState<WhiteLabelFileFieldState>({});
  const [favicon, setFavicon] = useState<WhiteLabelFileFieldState>({});
  const [loaderLogo, setLoaderLogo] = useState<WhiteLabelFileFieldState>({});

  const handleChangeCompanyDomain = (value: string) => {
    const error = handleTestRegex(whiteLabelFieldRegex.companyDomain, value);
    setCompanyDomain({
      value,
      error,
      errorMessage: error ? fieldErrorMessages.companyDomain : '',
    });
  };

  const handleChangeCompanyName = (value: string) => {
    const error = handleTestRegex(whiteLabelFieldRegex.companyName, value);
    setCompanyName({
      value,
      error,
      errorMessage: error ? fieldErrorMessages.companyName : '',
    });
  };

  const handleChangePrimary = (value: string) => {
    const error = handleTestRegex(whiteLabelFieldRegex.primaryColor, value);
    setPrimaryColor({
      value,
      error,
      errorMessage: error ? fieldErrorMessages.primaryColor : '',
    });
  };

  const handleChangeNotifBg = (value: string) => {
    const error = handleTestRegex(whiteLabelFieldRegex.notifBgColor, value);
    setNotifBg({
      value,
      error,
      errorMessage: error ? fieldErrorMessages.notifBgColor : '',
    });
  };

  const handleChangeHeaderLogo = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.files && event.target.files[0];
    if (value) {
      validateSvgFileDimensions(value, fileSize.headerLogo)
        .then(() => {
          setHeaderLogo({
            value,
            error: false,
          });
        })
        .catch(() => {
          setHeaderLogo({
            value,
            error: true,
            errorMessage: `Invalid header logo dimensions. Please upload a ${fileSize.headerLogo}x${fileSize.headerLogo}px SVG file.`,
          });
        });
    }
  };

  const handleChangeLoginLogo = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.files && event.target.files[0];
    if (value) {
      validateSvgFileDimensions(value, fileSize.loginLogo)
        .then(() => {
          setLoginLogo({
            value,
            error: false,
          });
        })
        .catch(() => {
          setLoginLogo({
            value,
            error: true,
            errorMessage: `Invalid header logo dimensions. Please upload a ${fileSize.loginLogo}x${fileSize.loginLogo}px SVG file.`,
          });
        });
    }
  };

  const handleChangeFavicon = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.files && event.target.files[0];
    if (value) {
      validateSvgFileDimensions(value, fileSize.favicon)
        .then(() => {
          setFavicon({
            value,
            error: false,
          });
        })
        .catch(() => {
          setFavicon({
            value,
            error: true,
            errorMessage: `Invalid header logo dimensions. Please upload a ${fileSize.favicon}x${fileSize.favicon}px SVG file.`,
          });
        });
    }
  };

  const handleChangeLoaderLogo = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.files && event.target.files[0];
    if (value) {
      validateSvgFileDimensions(value, fileSize.loaderLogo)
        .then(() => {
          setLoaderLogo({
            value,
            error: false,
          });
        })
        .catch(() => {
          setLoaderLogo({
            value,
            error: true,
            errorMessage: `Invalid header logo dimensions. Please upload a ${fileSize.loaderLogo}x${fileSize.loaderLogo}px SVG file.`,
          });
        });
    }
  };

  return (
    <Provider
      value={{
        companyDomain,
        companyName,
        primaryColor,
        notifBg,
        headerLogo,
        loginLogo,
        favicon,
        loaderLogo,
        handleChangeCompanyDomain,
        handleChangeCompanyName,
        handleChangePrimary,
        handleChangeNotifBg,
        handleChangeHeaderLogo,
        handleChangeLoginLogo,
        handleChangeFavicon,
        handleChangeLoaderLogo,
      }}
    >
      {children}
    </Provider>
  );
};
